import {
  Component,
  forwardRef,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SoSwitchUI } from '../so-switch/so-switch.component';
import { SoPremiumIconUI } from '../so-premium-icon/so-premium-icon.component';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

export type SoSwitchTileIconType = 'simple' | 'premium';
export type SoSwitchTileBackground = 'auto' | 'white' | string;

/**
 * Switch con estilo de tarjeta/lista.
 * 
 * Ideal para pantallas de configuración.
 * Basado en el componente SOSwitchTile de Flutter.
 * 
 * Uso:
 * ```html
 * <so-switch-tile
 *   title="Activar notificaciones"
 *   subtitle="Recibe alertas en tiempo real"
 *   icon="notifications"
 *   [(ngModel)]="isEnabled" />
 * ```
 */
@Component({
  selector: 'so-switch-tile-ui',
  standalone: true,
  imports: [CommonModule, SoSwitchUI, SoPremiumIconUI, SoSvgIconUI],
  templateUrl: './so-switch-tile.component.html',
  styleUrl: './so-switch-tile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoSwitchTileUI),
      multi: true
    }
  ]
})
export class SoSwitchTileUI implements ControlValueAccessor {
  // Required inputs
  title = input.required<string>();

  // Optional inputs
  subtitle = input<string>('');
  icon = input<string>('');
  iconType = input<SoSwitchTileIconType>('simple');
  iconColor = input<string>('--so-secondary');
  background = input<SoSwitchTileBackground>('auto');
  padding = input<number | null>(null);
  activeColor = input<string>('');
  disabled = input<boolean>(false);
  darkMode = input<boolean>(false);
  checked = input<boolean | undefined>(undefined);

  // Icon sizing
  iconSize = input<number>(20);
  iconContainerSize = input<number>(40);
  iconBorderRadius = input<number>(10);
  premiumIconPadding = input<number>(10);

  // Switch labels
  labels = input<boolean>(false);
  labelLeft = input<string>('');
  labelRight = input<string>('');

  // Outputs
  valueChanged = output<boolean>();

  // Internal state
  protected internalValue = signal<boolean>(false);
  private disabledByControl = signal<boolean>(false);
  protected effectiveDisabled = computed(() => this.disabled() || this.disabledByControl());

  // ControlValueAccessor callbacks
  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed: effective icon color (CSS variable or hex)
  protected effectiveIconColor = computed(() => {
    const color = this.iconColor();
    if (color.startsWith('--')) return `var(${color})`;
    if (color.startsWith('#')) return color;
    return `var(--${color})`;
  });

  // Computed: icon background color with opacity
  // Colores sincronizados con _colors.scss
  protected iconBgColor = computed(() => {
    const color = this.iconColor();
    // For CSS variables, we use a semi-transparent version
    if (color.startsWith('--') || !color.startsWith('#')) {
      const colorMap: Record<string, string> = {
        // Primary variants (azul)
        '--so-primary': 'rgba(0, 42, 127, 0.1)',
        '--so-primary-subtle': 'rgba(72, 82, 175, 0.1)',
        '--so-primary-soft': 'rgba(59, 89, 152, 0.1)',
        '--so-primary-thick': 'rgba(0, 1, 82, 0.1)',
        'so-primary': 'rgba(0, 42, 127, 0.1)',
        // Secondary variants (verde)
        '--so-secondary': 'rgba(163, 189, 54, 0.1)',
        '--so-secondary-subtle': 'rgba(215, 240, 104, 0.1)',
        '--so-secondary-thick': 'rgba(113, 141, 0, 0.1)',
        'so-secondary': 'rgba(163, 189, 54, 0.1)',
        // Danger variants
        '--so-danger': 'rgba(244, 67, 54, 0.1)',
        '--so-danger-subtle': 'rgba(255, 121, 97, 0.1)',
        '--so-danger-thick': 'rgba(186, 0, 13, 0.1)',
        'so-danger': 'rgba(244, 67, 54, 0.1)',
        // Warning variants
        '--so-warning': 'rgba(253, 216, 53, 0.1)',
        '--so-warning-subtle': 'rgba(255, 255, 107, 0.1)',
        '--so-warning-thick': 'rgba(198, 167, 0, 0.1)',
        'so-warning': 'rgba(253, 216, 53, 0.1)',
        // Success variants
        '--so-success': 'rgba(100, 221, 23, 0.1)',
        '--so-success-subtle': 'rgba(156, 255, 87, 0.1)',
        '--so-success-thick': 'rgba(31, 170, 0, 0.1)',
        'so-success': 'rgba(100, 221, 23, 0.1)',
        // Info variants
        '--so-info': 'rgba(0, 163, 235, 0.1)',
        '--so-info-subtle': 'rgba(33, 167, 226, 0.1)',
        'so-info': 'rgba(0, 163, 235, 0.1)',
        // Other colors
        '--so-pink': 'rgba(239, 0, 183, 0.1)',
        '--so-purple': 'rgba(87, 13, 248, 0.1)',
        '--so-orange': 'rgba(243, 140, 21, 0.1)',
        '--so-green': 'rgba(41, 166, 154, 0.1)',
        '--so-blue': 'rgba(0, 42, 127, 0.1)',
        'so-pink': 'rgba(239, 0, 183, 0.1)',
        'so-purple': 'rgba(87, 13, 248, 0.1)',
        'so-orange': 'rgba(243, 140, 21, 0.1)',
        'so-green': 'rgba(41, 166, 154, 0.1)',
        'so-blue': 'rgba(0, 42, 127, 0.1)',
      };
      return colorMap[color] || colorMap['--so-secondary'];
    }
    // For hex colors, add opacity
    return this.hexToRgba(color, 0.1);
  });

  // Computed: tile background CSS var
  protected tileBgCssVar = computed<string | null>(() => {
    const bg = this.background();
    if (!bg || bg === 'auto') return null;
    if (bg === 'white') return '#ffffff';
    return this.resolveHexOrCssVar(bg);
  });

  protected tileBorderCssVar = computed<string | null>(() => {
    const bg = this.background();
    if (!bg || bg === 'auto') return null;
    if (bg === 'white') return 'var(--so-gray, #e7e7e7)';
    return null;
  });

  protected tileTitleCssVar = computed<string | null>(() => {
    const bg = this.background();
    if (!bg || bg === 'auto') return null;
    if (bg === 'white') return 'var(--so-black, #0d0f11)';
    return null;
  });

  protected tileSubtitleCssVar = computed<string | null>(() => {
    const bg = this.background();
    if (!bg || bg === 'auto') return null;
    if (bg === 'white') return 'var(--so-gray-thick, #6c757d)';
    return null;
  });

  constructor() {
    // Sync checked input with internal state
    effect(() => {
      const checkedValue = this.checked();
      if (checkedValue !== undefined) {
        this.internalValue.set(checkedValue);
      }
    }, {  });
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean | null): void {
    this.internalValue.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByControl.set(isDisabled);
  }

  // Event handlers
  onTileClick(): void {
    if (this.effectiveDisabled()) return;
    this.toggle();
  }

  onSwitchChange(value: boolean): void {
    if (this.effectiveDisabled()) return;
    // Switch already handles the toggle, just sync state
    this.internalValue.set(value);
    this.onChange(value);
    this.onTouched();
    this.valueChanged.emit(value);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.onTileClick();
    }
  }

  private toggle(): void {
    const newValue = !this.internalValue();
    this.internalValue.set(newValue);
    this.onChange(newValue);
    this.onTouched();
    this.valueChanged.emit(newValue);
  }

  // Helper: convert hex to rgba
  private hexToRgba(hex: string, alpha: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const R = (num >> 16) & 255;
    const G = (num >> 8) & 255;
    const B = num & 255;
    return `rgba(${R}, ${G}, ${B}, ${alpha})`;
  }

  private resolveHexOrCssVar(color: string): string | null {
    const trimmed = color.trim();
    if (!trimmed) return null;

    // Hex only
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
      return trimmed;
    }

    // CSS variable name only (either "--token" or "token")
    if (/^--[a-zA-Z0-9_-]+$/.test(trimmed)) {
      return `var(${trimmed})`;
    }

    if (/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(trimmed)) {
      return `var(--${trimmed})`;
    }

    // Invalid value -> treat as auto
    return null;
  }
}
